// Shared bank-detail clipboard parser. Extracted from ChatHeaderActions.jsx
// (Coin Cut paste) so the staff-side "Create Withdraw Request" form can reuse
// the exact same battle-tested extraction.

// Well-known bank keywords → normalised bank names.
export const BANK_KEYWORDS = [
  ['state bank of india', 'State Bank of India'],
  ['sbi', 'State Bank of India'],
  ['hdfc', 'HDFC Bank'],
  ['icici', 'ICICI Bank'],
  ['axis', 'Axis Bank'],
  ['kotak', 'Kotak Mahindra Bank'],
  ['punjab national', 'Punjab National Bank'],
  ['pnb', 'Punjab National Bank'],
  ['bank of baroda', 'Bank of Baroda'],
  ['canara', 'Canara Bank'],
  ['union bank', 'Union Bank of India'],
  ['yes bank', 'Yes Bank'],
  ['idfc', 'IDFC First Bank'],
  ['indusind', 'IndusInd Bank'],
  ['idbi', 'IDBI Bank'],
  ['central bank', 'Central Bank of India'],
];

/**
 * Extract bank fields from a pasted blob (WhatsApp form dumps, etc.).
 * Handles patterns like:
 *   *ACCOUNT NO:*41939127506
 *   NAME: Parth Raval
 *   IFSC CODE: SBIN0016033
 *   BANK: State Bank of India
 *   UPI I'D — ravalparth578@oksbi
 * Only returns non-empty fields so callers can merge without wiping user input.
 */
export const parseBankFromPaste = (raw) => {
  if (!raw || typeof raw !== 'string') return {};
  // Normalise — strip markdown asterisks, weird bullets, CRs
  const text = raw.replace(/\*/g, ' ').replace(/[·•●]/g, ' ').replace(/[\r]+/g, '\n');
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

  const out = {};

  const labelMatch = (line, labels) => {
    const low = line.toLowerCase();
    for (const lbl of labels) {
      const idx = low.indexOf(lbl);
      if (idx !== -1) {
        const prev = idx === 0 ? '' : low[idx - 1];
        if (prev && /[a-z]/.test(prev)) continue;
        let rest = line.slice(idx + lbl.length);
        rest = rest.replace(/^[\s:\-–—=]+/, '').trim();
        rest = rest.split(/\s{2,}/)[0].trim();
        if (rest) return rest;
      }
    }
    return null;
  };

  // Pass 1 — account holder name.
  for (const line of lines) {
    if (out.account_holder_name) break;
    const strict = labelMatch(line, ['a/c holder name', 'account holder name', 'holder name', 'beneficiary name']);
    if (strict && !/^\d+$/.test(strict)) { out.account_holder_name = strict; break; }
  }
  if (!out.account_holder_name) {
    for (const line of lines) {
      const low = line.toLowerCase().trim();
      if (/^(user\s*name|bank\s*name|app\s*name|screen\s*name|login\s*name)\b/i.test(low)) continue;
      const v = labelMatch(line, ['name']);
      if (v && !/^\d+$/.test(v)) { out.account_holder_name = v; break; }
    }
  }

  // Pass 2 — bank account number.
  for (const line of lines) {
    if (out.bank_account_number) break;
    const v = labelMatch(line, ['bank account no', 'bank account number', 'account no', 'account number', 'acc no', 'a/c no', 'a/c number']);
    if (v) {
      const digits = v.replace(/\D/g, '');
      if (digits.length >= 6) { out.bank_account_number = digits; break; }
    }
  }

  // Pass 3 — IFSC (label first, whole-text regex as fallback).
  for (const line of lines) {
    if (out.ifsc_code) break;
    const v = labelMatch(line, ['ifsc code', 'ifsc']);
    if (v) {
      const m = v.toUpperCase().match(/[A-Z]{4}0[A-Z0-9]{6}/);
      if (m) { out.ifsc_code = m[0]; break; }
    }
  }
  if (!out.ifsc_code) {
    const m = text.toUpperCase().match(/[A-Z]{4}0[A-Z0-9]{6}/);
    if (m) out.ifsc_code = m[0];
  }

  // Pass 4 — bank name.
  for (const line of lines) {
    if (out.bank_name) break;
    const v = labelMatch(line, ['bank name', 'bank']);
    if (v && v.length <= 60 && !/^account/i.test(v)) { out.bank_name = v; break; }
  }

  // Pass 5 — UPI ID.
  for (const line of lines) {
    if (out.upi_id) break;
    const v = labelMatch(line, ["upi i'd", 'upi id', 'upi']);
    if (v && v.includes('@')) { out.upi_id = v.split(/\s+/)[0]; break; }
  }
  if (!out.upi_id) {
    const m = text.match(/[\w.\-]+@[\w.\-]+/);
    if (m && /@(?:ybl|okhdfcbank|okaxis|oksbi|okicici|paytm|apl|ibl|axl|axisbank|hdfcbank|sbi|upi|allbank|federal|kotak|barodampay|indus|jio)\b/i.test(m[0])) {
      out.upi_id = m[0];
    }
  }

  // Bank name: infer from IFSC prefix or keyword scan when the form didn't
  // include a "Bank Name:" line.
  const IFSC_MAP = { SBIN: 'State Bank of India', HDFC: 'HDFC Bank', ICIC: 'ICICI Bank', UTIB: 'Axis Bank', KKBK: 'Kotak Mahindra Bank', PUNB: 'Punjab National Bank', BARB: 'Bank of Baroda', CNRB: 'Canara Bank', UBIN: 'Union Bank of India', YESB: 'Yes Bank', IDFB: 'IDFC First Bank', INDB: 'IndusInd Bank', IBKL: 'IDBI Bank', CBIN: 'Central Bank of India' };
  if (out.bank_name) {
    const low = out.bank_name.toLowerCase();
    for (const [kw, name] of BANK_KEYWORDS) {
      if (low === kw || low.startsWith(kw)) { out.bank_name = name; break; }
    }
  } else {
    if (out.ifsc_code) {
      const code = out.ifsc_code.slice(0, 4).toUpperCase();
      if (IFSC_MAP[code]) out.bank_name = IFSC_MAP[code];
    }
    if (!out.bank_name) {
      const low = text.toLowerCase();
      for (const [kw, name] of BANK_KEYWORDS) {
        const re = new RegExp(`(^|[^a-z])${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z]|$)`, 'i');
        if (re.test(low)) { out.bank_name = name; break; }
      }
    }
  }

  return out;
};
